/**
 * Bridge between app dates and MUI X pickers / react-big-calendar.
 *
 * `AdapterDateFns` and `dateFnsLocalizer` read and write plain `Date`s in the browser's time
 * zone and cannot be pointed at another one. To show Paris time whatever the browser's time
 * zone, they receive a "picker date": a `Date` whose browser wall-clock equals the Paris
 * wall-clock of the instant (or browser midnight of a calendar date). Convert back with the
 * matching `from*` function before storing or sending the value. This is the only module of
 * the admin that calls date-fns directly.
 */
import {
  type CalendarDate,
  type DateInput,
  fromDateTimeLocalInput,
  isCalendarDate,
  toCalendarDate,
  toDate,
  toDateTimeLocalInput,
} from "@repo/utils/date";
import { format, getDay, isValid, parse, parseISO, startOfWeek } from "date-fns";

const DATE_TIME_LOCAL = "yyyy-MM-dd'T'HH:mm";
const CALENDAR_DATE = "yyyy-MM-dd";

/** Instant -> picker date showing its Paris wall-clock time. */
export function toPickerDate(value: DateInput | null | undefined): Date | null {
  const local = toDateTimeLocalInput(value);
  if (!local) return null;
  const date = parseISO(local);
  return isValid(date) ? date : null;
}

/** Picker date -> instant at that Paris wall-clock time (minute precision). */
export function fromPickerDate(date: Date | null | undefined): Date | null {
  if (!date || !isValid(date)) return null;
  return fromDateTimeLocalInput(format(date, DATE_TIME_LOCAL));
}

/** Instant at the day of `day` and the time of `time`, both picker dates, in Paris. */
export function combinePickerDateAndTime(day: Date, time: Date): Date | null {
  if (!isValid(day) || !isValid(time)) return null;
  return fromDateTimeLocalInput(`${format(day, CALENDAR_DATE)}T${format(time, "HH:mm")}`);
}

/** Calendar date (or instant, read as its Paris day) -> picker date at browser midnight. */
export function calendarDateToPickerDate(value: DateInput | null | undefined): Date | null {
  if (isCalendarDate(value)) return parseISO(value);
  const date = toDate(value);
  return date ? parseISO(toCalendarDate(date)) : null;
}

/** Picker date -> the calendar date it shows. */
export function pickerDateToCalendarDate(date: Date | null | undefined): CalendarDate | null {
  if (!date || !isValid(date)) return null;
  return format(date, CALENDAR_DATE);
}

// ---------------------------------------------------------------------------
// date-fns objects required by third-party components

/**
 * `pickerLocale`: locale of the MUI X `LocalizationProvider`.
 * `calendarLocaleEnUS`: locale of the react-big-calendar localizer.
 */
export { enUS as calendarLocaleEnUS, fr as pickerLocale } from "date-fns/locale";

/** Functions `dateFnsLocalizer` of react-big-calendar needs; it works on picker dates. */
export const calendarLocalizerFunctions = { format, getDay, parse, startOfWeek };
